/** Color Conversion Utilities Handles conversions between HSVA, RGB, HEX, and HSL color formats */

export type HSVA = {
  h: number; // 0-360
  s: number; // 0-1
  v: number; // 0-1
  a: number; // 0-1
};

export type RGBA = {
  r: number; // 0-1
  g: number; // 0-1
  b: number; // 0-1
  a: number; // 0-1
};

export type HSLA = {
  h: number; // 0-360
  s: number; // 0-1
  l: number; // 0-1
  a: number; // 0-1
};

/** Convert HSV to RGB */
export function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const hh = (h % 360) / 60;
  const c = v * s;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = v - c;

  let r = 0;
  let g = 0;
  let b = 0;
  if (hh >= 0 && hh < 1) {
    [r, g, b] = [c, x, 0];
  } else if (hh >= 1 && hh < 2) {
    [r, g, b] = [x, c, 0];
  } else if (hh >= 2 && hh < 3) {
    [r, g, b] = [0, c, x];
  } else if (hh >= 3 && hh < 4) {
    [r, g, b] = [0, x, c];
  } else if (hh >= 4 && hh < 5) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }

  return { r: r + m, g: g + m, b: b + m };
}

/** Convert RGB to HSV */
export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  const s = max === 0 ? 0 : delta / max;
  const v = max;

  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    } else if (max === g) {
      h = ((b - r) / delta + 2) / 6;
    } else {
      h = ((r - g) / delta + 4) / 6;
    }
  }

  return { h: h * 360, s, v };
}

/** Convert HSVA to HEX string */
export function hsvaToHex(hsva: HSVA): string {
  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, '0');
  let hex = `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;

  // Add alpha if not fully opaque
  if (hsva.a < 1) {
    hex += toHex(hsva.a);
  }

  return hex.toUpperCase();
}

/** Convert HSVA to RGBA string */
export function hsvaToRgbaString(hsva: HSVA): string {
  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  const r = Math.round(rgb.r * 255);
  const g = Math.round(rgb.g * 255);
  const b = Math.round(rgb.b * 255);
  return `rgba(${r}, ${g}, ${b}, ${hsva.a.toFixed(2)})`;
}

/** Convert HSVA to HSLA string */
export function hsvaToHslaString(hsva: HSVA): string {
  const hsl = hsvToHsl(hsva.h, hsva.s, hsva.v);
  return `hsla(${Math.round(hsl.h)}, ${Math.round(hsl.s * 100)}%, ${Math.round(hsl.l * 100)}%, ${hsva.a.toFixed(2)})`;
}

/** Convert HSV to HSL */
export function hsvToHsl(h: number, s: number, v: number): { h: number; s: number; l: number } {
  const l = v * (1 - s / 2);
  const sL = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);
  return { h, s: sL, l };
}

/** Convert HSL to HSV */
export function hslToHsv(h: number, s: number, l: number): { h: number; s: number; v: number } {
  const v = l + s * Math.min(l, 1 - l);
  const sV = v === 0 ? 0 : 2 * (1 - l / v);
  return { h, s: sV, v };
}

/** Parse color string to HSVA Supports: HEX (#RGB, #RRGGBB, #RRGGBBAA), RGB(A), HSL(A), named colors */
export function parseColorToHsva(color: string): HSVA | null {
  const trimmed = color.trim().toLowerCase();

  // HEX format
  if (trimmed.startsWith('#')) {
    return parseHexToHsva(trimmed);
  }

  // RGB/RGBA format
  if (trimmed.startsWith('rgb')) {
    return parseRgbToHsva(trimmed);
  }

  // HSL/HSLA format
  if (trimmed.startsWith('hsl')) {
    return parseHslToHsva(trimmed);
  }

  // Named colors (basic support)
  const namedColor = parseNamedColor(trimmed);
  if (namedColor) {
    return parseHexToHsva(namedColor);
  }

  return null;
}

function parseHexToHsva(hex: string): HSVA | null {
  let cleanHex = hex.replace('#', '');

  // Expand shorthand (#RGB -> #RRGGBB)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  if (cleanHex.length !== 6 && cleanHex.length !== 8) {
    return null;
  }

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
  const a = cleanHex.length === 8 ? parseInt(cleanHex.substring(6, 8), 16) / 255 : 1;

  const hsv = rgbToHsv(r, g, b);
  return { ...hsv, a };
}

function parseRgbToHsva(rgb: string): HSVA | null {
  const match = rgb.match(/rgba?\(([^)]+)\)/);
  if (!match || !match[1]) {
    return null;
  }

  const parts = match[1].split(',').map((s) => s.trim());
  if (parts.length < 3) {
    return null;
  }

  const r = parseFloat(parts[0]!) / 255;
  const g = parseFloat(parts[1]!) / 255;
  const b = parseFloat(parts[2]!) / 255;
  const a = parts.length === 4 ? parseFloat(parts[3]!) : 1;

  const hsv = rgbToHsv(r, g, b);
  return { ...hsv, a };
}

function parseHslToHsva(hsl: string): HSVA | null {
  const match = hsl.match(/hsla?\(([^)]+)\)/);
  if (!match || !match[1]) {
    return null;
  }

  const parts = match[1].split(',').map((s) => s.trim());
  if (parts.length < 3) {
    return null;
  }

  const h = parseFloat(parts[0]!);
  const s = parseFloat(parts[1]!) / 100;
  const l = parseFloat(parts[2]!) / 100;
  const a = parts.length === 4 ? parseFloat(parts[3]!) : 1;

  const hsv = hslToHsv(h, s, l);
  return { ...hsv, a };
}

function parseNamedColor(name: string): string | null {
  const namedColors: Record<string, string> = {
    red: '#FF0000',
    green: '#008000',
    blue: '#0000FF',
    white: '#FFFFFF',
    black: '#000000',
    yellow: '#FFFF00',
    cyan: '#00FFFF',
    magenta: '#FF00FF',
    transparent: '#00000000',
  };
  return namedColors[name] || null;
}
